import { createTheme, PaletteColor, PaletteColorOptions } from "@mui/material/styles";
import { CSSProperties } from "react";
import { createTypography } from "./typography";
import { WHITE_LABEL_COLORS } from "./colors";
import { FONT_WEIGHTS } from "./fontWeights";
import { SelectIcon } from "@common/SelectIcon/SelectIcon";
import { CheckboxCheckedIcon, CheckboxUncheckedIcon } from "@common/CheckboxIcons";
import { RadioCheckedIcon, RadioUncheckedIcon } from "@common/RadioIcons";
import { Theme } from "@mui/material";
import { shadows } from "./shadows";
import { EDeviceType, getThemeBreakpointByDeviceType } from "@utils/getThemeBreakpointByDeviceType";

// Used to add some color properties
declare module "@mui/material/styles" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypeBackground {
        main?: string;
        dark?: string;
        light?: string;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypeBorder {
        main: string;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Palette {
        tertiary: PaletteColor;
        quaternary: PaletteColor;
        border: TypeBorder;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface PaletteOptions {
        tertiary?: PaletteColorOptions;
        quaternary?: PaletteColorOptions;
        border?: Partial<TypeBorder>;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyVariants {
        large: CSSProperties;
        normal: CSSProperties;
        small: CSSProperties;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyVariantsOptions {
        large?: CSSProperties;
        normal?: CSSProperties;
        small?: CSSProperties;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Typography {
        fontWeight: typeof FONT_WEIGHTS;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyOptions {
        fontWeight?: typeof FONT_WEIGHTS;
    }
}

declare module "@mui/material/Typography" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyPropsVariantOverrides {
        large: true;
        normal: true;
        small: true;
    }
}

type CustomTypographyVariant = "large" | "normal" | "small";

const VARIANT_MAPPING: Record<CustomTypographyVariant, string> = {
    large: "p",
    normal: "p",
    small: "p",
};

export const PLACEHOLDER_OPACITY = 0.4;

const BASE_BORDER_RADIUS = 12;

const getSharedDropdownPaperStyles = (theme: Theme) => ({
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
    border: `1px solid ${theme.palette.secondary.main}`,
    boxShadow: "none",
    marginTop: 0,

    "& .MuiList-root": {
        paddingTop: 0,
        paddingBottom: 0,
    },
});

const getSharedDropdownItemStyles = (theme: Theme) => ({
    backgroundColor: theme.palette.background.main,
    color: theme.palette.secondary.main,
    fontSize: 16,
    fontWeight: FONT_WEIGHTS.light,

    "&:hover": {
        backgroundColor: theme.palette.background.dark,
    },
    "&.Mui-selected": {
        backgroundColor: theme.palette.background.dark,
    },
    "&.Mui-selected:hover": {
        backgroundColor: theme.palette.background.dark,
    },
    "&.Mui-disabled p": {
        fontWeight: FONT_WEIGHTS.light,
    },
});

const breakpointsTheme = createTheme();

export const muiTheme = createTheme({
    shadows,
    palette: {
        primary: {
            main: WHITE_LABEL_COLORS.primary,
            contrastText: WHITE_LABEL_COLORS.white,
        },
        secondary: {
            main: WHITE_LABEL_COLORS.secondary,
            contrastText: WHITE_LABEL_COLORS.white,
        },
        tertiary: {
            main: WHITE_LABEL_COLORS.tertiary,
            contrastText: WHITE_LABEL_COLORS.secondary,
        },
        quaternary: {
            main: WHITE_LABEL_COLORS.quaternary,
            contrastText: WHITE_LABEL_COLORS.secondary,
        },
        error: {
            main: WHITE_LABEL_COLORS.error,
        },
        background: {
            main: WHITE_LABEL_COLORS.neutral1,
            dark: WHITE_LABEL_COLORS.neutral2,
            light: WHITE_LABEL_COLORS.white,
        },
        border: {
            main: WHITE_LABEL_COLORS.neutral2,
        },
    },
    shape: {
        borderRadius: BASE_BORDER_RADIUS,
    },
    typography: createTypography(breakpointsTheme.breakpoints),
    components: {
        MuiTextField: {
            defaultProps: {
                variant: "outlined",
                size: "small",
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.background.light,
                    borderRadius: theme.shape.borderRadius,

                    "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${theme.palette.secondary.main}`,
                        transition: "0.20s",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: "none",
                    },
                    ".LabeledTextField-readOnly &": {
                        backgroundColor: theme.palette.background.main,
                    },
                    ".LabeledTextField-readOnly & .MuiOutlinedInput-notchedOutline": {
                        border: 0,
                    },
                    ".LabeledTextField-readOnly &.Mui-disabled": {
                        opacity: 1,
                    },
                    ".LabeledTextField-readOnly & .MuiOutlinedInput-input": {
                        padding: theme.spacing(1, 1.5),
                    },
                    ".LabeledTextField-readOnly & .MuiInputBase-input": {
                        ...theme.typography.normal,
                    },
                    ".LabeledTextField-readOnly & .MuiInputBase-input.Mui-disabled": {
                        color: theme.palette.secondary.main,
                        WebkitTextFillColor: theme.palette.secondary.main,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${theme.palette.secondary.main}`,
                        boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${theme.palette.error.main}`,
                        boxShadow: `0 0 0 1px ${theme.palette.error.main}`,
                    },
                    "& .MuiSelect-select[aria-expanded='true'] ~ .MuiOutlinedInput-notchedOutline": {
                        borderBottom: 0,
                        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
                    },
                }),
            },
        },
        MuiSelect: {
            defaultProps: {
                IconComponent: SelectIcon,
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&.has-options.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderBottom: 0,
                        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
                    },
                }),
                paper: ({ theme }) => ({
                    "&.has-options": getSharedDropdownPaperStyles(theme),
                }),
                listbox: ({ theme }) => ({
                    padding: 0,
                    ...getSharedDropdownItemStyles(theme),
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: ({ theme }) => getSharedDropdownPaperStyles(theme),
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => getSharedDropdownItemStyles(theme),
            },
        },
        MuiPopper: {
            styleOverrides: {
                root: {
                    "& .MuiAutocomplete-paper": {
                        marginTop: 0,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                    fontSize: 16,
                    fontWeight: FONT_WEIGHTS.light,

                    "&.Mui-focused": {
                        color: theme.palette.secondary.main,
                    },
                    "&.Mui-error": {
                        color: theme.palette.error.main,
                    },
                }),
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 14,
                    fontWeight: FONT_WEIGHTS.light,
                    lineHeight: 1.5,
                    marginTop: theme.spacing(1),
                    marginLeft: 0,

                    ".LabeledTextField-readOnly &": {
                        color: theme.palette.secondary.main,
                        opacity: 1,
                    },
                    ".LabeledTextField-readOnly &.Mui-disabled": {
                        color: theme.palette.secondary.main,
                        opacity: 1,
                    },
                    "&.Mui-error": {
                        color: theme.palette.error.main,
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                size: "large",
                variant: "contained",
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: theme.spacing(1, 2),
                    textTransform: "initial",
                    fontSize: 14,
                    fontWeight: theme.typography.fontWeightLight,
                    borderRadius: "50px",
                    height: 40,
                    whiteSpace: "nowrap",
                    [getThemeBreakpointByDeviceType(theme, EDeviceType.MOBILE)]: {
                        fontSize: 16,
                    },
                }),
                contained: {
                    boxShadow: "none",
                },
                outlined: {
                    boxShadow: "none",
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    "&.Mui-focusVisible": {
                        outline: "auto 5px -webkit-focus-ring-color !important",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    border: `1px solid ${theme.palette.border.main}`,
                }),
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: theme.spacing(2),
                }),
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    padding: "24px 0",
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&.Mui-active": {
                        fill: theme.palette.secondary.main,
                    },
                    "&.Mui-completed": {
                        fill: theme.palette.secondary.main,
                    },
                    fill: theme.palette.background.dark,
                    "& .MuiStepIcon-text": {
                        fill: theme.palette.background.light,
                    },
                }),
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: ({ theme }) => ({
                    color: theme.palette.background.dark,
                    "&.Mui-active": {
                        color: theme.palette.secondary.main,
                    },
                    "&.Mui-completed": {
                        color: theme.palette.secondary.main,
                    },
                }),
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                }),
            },
        },
        MuiMobileStepper: {
            styleOverrides: {
                root: {
                    backgroundColor: "transparent",
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                underline: {
                    "&&&&:hover:before": {
                        borderBottom: "1px solid",
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                    fontSize: 16,
                    fontWeight: FONT_WEIGHTS.light,

                    "& .MuiInputBase-input p": {
                        opacity: 0.7,
                        fontSize: 16,
                        fontWeight: FONT_WEIGHTS.light,
                    },
                    "& .MuiInputBase-input::placeholder": {
                        opacity: 0.7,
                        fontSize: 16,
                        fontWeight: FONT_WEIGHTS.light,
                    },
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                body: {
                    fontSize: "initial",
                },
                footer: {
                    fontSize: "initial",
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: "0.7em",
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: 4,
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: "secondary",
                checkedIcon: <CheckboxCheckedIcon />,
                icon: <CheckboxUncheckedIcon />,
            },
            styleOverrides: {
                root: {
                    width: 22,
                    height: 22,
                    padding: 0,
                    marginRight: 8,
                    "& .MuiSvgIcon-root": {
                        width: 22,
                        height: 22,
                        fontSize: 22,
                    },
                },
            },
        },
        MuiRadio: {
            defaultProps: {
                color: "secondary",
                checkedIcon: <RadioCheckedIcon />,
                icon: <RadioUncheckedIcon />,
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                    fontSize: "0.8em",
                    fontWeight: theme.typography.fontWeightLight,
                }),
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecorationColor: "currentColor",
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: VARIANT_MAPPING,
            },
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const variantStyles = theme.typography[ownerState.variant as CustomTypographyVariant];

                    return {
                        fontFamily: (variantStyles?.fontFamily as string) ?? theme.typography.fontFamily,
                    };
                },
            },
        },
    },
});
